import { useAtom } from 'jotai';

import { classPlanningFiltersAtom } from '../store/atoms/classPlanningFiltersAtom';
import {
  type ClassPlanningFilters,
  INITIAL_CLASS_PLANNING_FILTERS,
} from '../types/classPlanningFilters';

/**
 * Hook para gerenciar os filtros da listagem de planejamento de aulas.
 *
 * @returns Filtros atuais e funções para atualizar e resetar.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, resetFilters } = useClassPlanningFilters();
 * updateFilters({ companyId: 1, companyLabel: 'MBX', page: 1 });
 * ```
 */
export function useClassPlanningFilters() {
  const [filters, setFilters] = useAtom(classPlanningFiltersAtom);

  /**
   * Atualiza parcialmente os filtros. Reseta para a primeira página, exceto
   * quando o próprio `page` é fornecido no `partial` (navegação de páginas).
   */
  const updateFilters = (partial: Partial<ClassPlanningFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  /** Limpa todos os filtros e volta para a primeira página. */
  const resetFilters = () => {
    setFilters(INITIAL_CLASS_PLANNING_FILTERS);
  };

  return { filters, updateFilters, resetFilters } as const;
}
